/**
 * dateRanges — one definition of "this week" for the whole page.
 *
 * The filter and the list groups used to compute periods independently and
 * disagree about what the same word meant:
 *
 *   filter  "This Week"  = since Sunday of the current calendar week
 *   grouping "This Week" = anything within the last 7 days (rolling)
 *
 * So on a Thursday, a recording from the previous Saturday was labelled
 * "This Week" in the list but excluded by the "This Week" filter. Same for
 * "This Month" (calendar 1st vs. a rolling 30 days). Both now derive from the
 * boundaries below, so a group label always means what the filter of the same
 * name selects.
 *
 * Weeks start Sunday, matching the previous filter behaviour and US convention.
 */

export const DATE_RANGES = [
  { id: 'all', label: 'All Time' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: 'This Quarter' },
];

/**
 * Parse a recorded_at value, or null when it's missing OR unparseable.
 *
 * Both cases have to collapse to the same answer. Previously a garbage date
 * string was dropped by the range filter (NaN fails every comparison) but
 * grouped under "Earlier" in All Time — the same row in two different states
 * depending on which filter was active.
 */
export function parseRecordedAt(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Period starts, computed once so a render can't straddle midnight. */
export function periodBoundaries(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // back to Sunday

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);

  return { today, yesterday, weekStart, monthStart, quarterStart };
}

/** Inclusive start of a range id, or null for "all" (no bound). */
export function rangeStart(rangeId, now = new Date()) {
  const b = periodBoundaries(now);
  switch (rangeId) {
    case 'today': return b.today;
    case 'week': return b.weekStart;
    case 'month': return b.monthStart;
    case 'quarter': return b.quarterStart;
    default: return null;
  }
}

/**
 * Filter to a range. Recordings with no usable date are excluded from every
 * bounded range — an undated row can't be shown to fall inside one. They are
 * NOT silently lost: the page counts them and offers a way back to All Time.
 */
export function filterByDateRange(recordings, rangeId, now = new Date()) {
  const start = rangeStart(rangeId, now);
  if (!start) return recordings;
  return recordings.filter(r => {
    const d = parseRecordedAt(r.recorded_at);
    return d !== null && d >= start;
  });
}

/** How many of these have no usable date — i.e. what a bounded range hides. */
export function countUndated(recordings) {
  return recordings.reduce((n, r) => n + (parseRecordedAt(r.recorded_at) ? 0 : 1), 0);
}

/**
 * Group labels, widest-last. Every bounded range in DATE_RANGES has a matching
 * group, so filtering to "This Quarter" yields groups that nest inside it
 * rather than a bare "Earlier" bucket.
 */
export const GROUP_ORDER = [
  'Today',
  'Yesterday',
  'This Week',
  'This Month',
  'This Quarter',
  'Earlier',
  'Undated',
];

/**
 * Which group a recording belongs to, given boundaries from periodBoundaries().
 *
 * A future-dated recording lands in "Today" (it clears every boundary). That's
 * a data bug rather than a real state, and the top of the list is where you'd
 * actually notice it — the old rolling-window maths quietly filed it under
 * "This Week".
 */
export function getDateGroup(recording, boundaries) {
  const d = parseRecordedAt(recording.recorded_at);
  if (!d) return 'Undated';

  if (d >= boundaries.today) return 'Today';
  if (d >= boundaries.yesterday) return 'Yesterday';
  if (d >= boundaries.weekStart) return 'This Week';
  if (d >= boundaries.monthStart) return 'This Month';
  if (d >= boundaries.quarterStart) return 'This Quarter';
  return 'Earlier';
}

/** Group a list into non-empty, ordered date groups. */
export function groupByDate(recordings, now = new Date()) {
  const boundaries = periodBoundaries(now);
  const groups = new Map(GROUP_ORDER.map(label => [label, []]));

  recordings.forEach(recording => {
    groups.get(getDateGroup(recording, boundaries)).push(recording);
  });

  return GROUP_ORDER
    .map(label => ({ label, items: groups.get(label) }))
    .filter(g => g.items.length > 0);
}
